import React, { useState, useEffect } from 'react';
import { Users, Plus, Mail, Key, UserCog } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('front_desk');

  useEffect(() => {
    fetchUsers();
  }, []);

  const getToken = () => localStorage.getItem('access_token');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/org/users`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const response = await axios.post(`${API}/org/users`, {
        name,
        email,
        role
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      // Show temp password
      setTempPassword(response.data.temp_password);
      setShowPassword(true);
      
      toast.success('User created successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      await axios.patch(`${API}/org/users/${userId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'disabled'}`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/org/users/${userId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700',
      manager: 'bg-blue-100 text-blue-700',
      front_desk: 'bg-cyan-100 text-cyan-700',
      housekeeping: 'bg-green-100 text-green-700',
      maintenance: 'bg-orange-100 text-orange-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-2">
            <Users className="text-cyan-600" size={32} />
            User Management
          </h1>
          <p className="text-slate-600 mt-2">
            Manage staff users and their access to your properties
          </p>
        </div>
        
        <Dialog open={openDialog} onOpenChange={(open) => {
          setOpenDialog(open);
          if (!open) {
            setName('');
            setEmail('');
            setRole('front_desk');
            setShowPassword(false);
            setTempPassword('');
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-cyan-600 to-blue-600 gap-2">
              <Plus size={18} />
              Add Staff User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Staff User</DialogTitle>
            </DialogHeader>
            
            {showPassword ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium mb-2">✓ User created successfully!</p>
                  <p className="text-sm text-slate-600 mb-3">
                    Share these credentials with the staff member:
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-slate-500" />
                      <span className="text-sm font-medium">Email:</span>
                      <span className="text-sm">{email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Key size={16} className="text-slate-500" />
                      <span className="text-sm font-medium">Temp Password:</span>
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                        {tempPassword}
                      </code>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    ⚠️ Save this password - it cannot be recovered. The user can change it after first login.
                  </p>
                </div>
                <Button onClick={() => {
                  setOpenDialog(false);
                  setShowPassword(false);
                  setTempPassword('');
                }} className="w-full">
                  Done
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@hotel.com"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger id="role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="front_desk">Front Desk</SelectItem>
                      <SelectItem value="housekeeping">Housekeeping</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    A temporary password will be generated for the user
                  </p>
                </div>
                <Button onClick={createUser} className="w-full">
                  Create User
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Organization Users</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-slate-600">Loading users...</p>
          ) : users.length > 0 ? (
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {user.status === 'disabled' && (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {user.role !== 'owner' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateUserStatus(user.id, user.status === 'active' ? 'disabled' : 'active')}
                      >
                        {user.status === 'active' ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteUser(user.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <UserCog className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-600">No staff users yet</p>
              <p className="text-sm text-slate-500 mt-2">
                Add front desk, housekeeping, and maintenance staff
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-cyan-200 bg-cyan-50/50">
        <CardContent className="pt-6">
          <p className="text-sm text-slate-700">
            <strong>💡 Tip:</strong> You can add unlimited staff users at no extra cost. Each user will receive their own login credentials and access based on their role.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;